import { Box, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { Link, useLocation } from 'react-router';
import { Fragment, useMemo } from 'react';
import { osirisTokens } from '@/theme/tokens';

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbsProps {
  crumbs?: Crumb[];
}

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  admin: 'Admin',
  drones: 'Drones / Stations',
  apps: 'Apps',
  'apps-leaderboard': 'Apps',
  'live-sessions': 'Live Session',
  sessions: 'Session',
  missions: 'Missions',
  new: 'New',
  'trigger-templates': 'Triggers',
  'swarm-behaviors': 'Swarm Behaviors',
  'image-annotations': 'Annotations',
  'training-jobs': 'Training Jobs',
  'controller-check': 'Controller',
  'fpv-sim': 'FPV Simulator',
  users: 'Users',
  logs: 'Logs',
  install: 'Install',
};

function labelFor(segment: string): string {
  if (ROUTE_LABELS[segment]) return ROUTE_LABELS[segment];
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

export function Breadcrumbs({ crumbs }: BreadcrumbsProps) {
  const location = useLocation();

  const derived = useMemo<Crumb[]>(() => {
    if (crumbs) return crumbs;
    const segments = location.pathname.split('/').filter(Boolean);
    return segments.map((seg, idx) => ({
      label: labelFor(seg),
      to: idx < segments.length - 1 ? '/' + segments.slice(0, idx + 1).join('/') : undefined,
    }));
  }, [crumbs, location.pathname]);

  if (derived.length === 0) return null;

  const head = derived[0];
  const tail = derived.slice(1);

  return (
    <Box
      className="topbar-breadcrumbs"
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        pl: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Typography
        sx={{
          fontFamily: osirisTokens.fonts.display,
          fontSize: 16,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: tail.length === 0 ? osirisTokens.text.primary : osirisTokens.text.secondary,
        }}
      >
        {head.to ? (
          <Box
            component={Link}
            to={head.to}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { color: osirisTokens.text.active },
            }}
          >
            {head.label}
          </Box>
        ) : (
          head.label
        )}
      </Typography>
      {tail.map((crumb, idx) => {
        const last = idx === tail.length - 1;
        return (
          <Fragment key={`${crumb.label}-${idx}`}>
            <ChevronRight sx={{ fontSize: 18, mx: 0.5, color: osirisTokens.icons.tertiary }} />
            <Box
              sx={{
                fontFamily: osirisTokens.fonts.body,
                fontSize: 14,
                lineHeight: 1.2,
                color: last ? osirisTokens.text.primary : osirisTokens.text.secondary,
                textTransform: 'capitalize',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                wordBreak: 'break-all',
              }}
            >
              {crumb.to && !last ? (
                <Box
                  component={Link}
                  to={crumb.to}
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { color: osirisTokens.text.active },
                  }}
                >
                  {crumb.label}
                </Box>
              ) : (
                crumb.label
              )}
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
}
